import React, { useEffect, useState } from 'react';
import Layout from '../Layout';
import { Typography, Container, Box, Grid, TextField, Button, CircularProgress } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CustomBreadcrumbs from '../Breadcrumb';
import { createBroadcastChannel, getBroadcastChannels } from '../../ReduxStore/NotificationSlice';
import {
    BROADCAST_CHANNEL_LIMITS,
    sanitizeForDisplay,
    validateBroadcastChannelField,
} from '../../utils/inputSanitization';

const AddBroadCastChannel = () => {
    const dispatch = useDispatch();
    const breadcrumbsLinks = [
        { label: 'Home', href: '/' },
        { label: 'Settings', href: '/settings' },
        { label: 'Mail Management', href: '/mail-management' }
    ];

    const [loadings, setLoading] = useState(true);
    const [broadcastChannels, setChannels] = useState([]);

    const [formData, setFormData] = useState({
        name: "",
        description: ""
    });

    const [formErrors, setFormErrors] = useState({
        name: "",
        description: ""
    });

    const validateField = (name, value) => {
        const result = validateBroadcastChannelField(name, value);
        setFormErrors((prev) => ({
            ...prev,
            [name]: result.valid ? "" : result.message,
        }));
        return result;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        validateField(name, value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const nameResult = validateField('name', formData.name);
        const descriptionResult = validateField('description', formData.description);

        if (!nameResult.valid || !descriptionResult.valid) {
            toast.error("Please fix validation errors before submitting.");
            return;
        }

        const payload = {
            customer_id: "af33f9117ecb4d8d8def84d6331eebd4",
            description: descriptionResult.sanitized,
            name: nameResult.sanitized
        };

        try {
            const action = dispatch(createBroadcastChannel(payload));
            const response = await action.unwrap();
            if (response.StatusCode === 201) {
                toast.success("Broadcast Channel Added!");
                getChannels();
                setFormData({
                    name: "",
                    description: ""
                });
                setFormErrors({ name: "", description: "" });
            }
        } catch (error) {
            toast.error(error.message || "Something went wrong!");
        }
    };

    const getChannels = async () => {
        try {
            const action = dispatch(getBroadcastChannels(`?customer_id=af33f9117ecb4d8d8def84d6331eebd4`));
            const res = await action.unwrap();
            if (res.result) {
                setChannels(res.result);
            }
        } catch (error) {
            console.error("Error in okHandler:", error);
            toast.error(error.message || "No Logs Found!");
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            await getChannels();
            setLoading(false);
        };
        fetchData();
    }, []);

    const hasFormErrors = Boolean(formErrors.name || formErrors.description);

    return (
        <Layout>
            <Container>
                <Box mt={1} mb={2}>
                    <CustomBreadcrumbs links={breadcrumbsLinks} current="Add Broadcast Channel" />
                </Box>
                <Box className="add-checkpoint" mb={3}>
                    <Grid container className='row1' spacing={3}>
                        <Grid item xs={12} sm={3}>
                            <Typography variant="body1" style={{ fontSize: '16px', fontWeight: 500 }}>
                                Add Channel
                            </Typography>
                        </Grid>
                    </Grid>
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    label="Channel Name"
                                    name="name"
                                    type="text"
                                    variant="outlined"
                                    fullWidth
                                    required
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    onBlur={(e) => validateField(e.target.name, e.target.value)}
                                    error={!!formErrors.name}
                                    helperText={formErrors.name}
                                    inputProps={{
                                        maxLength: BROADCAST_CHANNEL_LIMITS.name.max,
                                        autoComplete: 'off',
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    label="Channel Description"
                                    name="description"
                                    type="text"
                                    variant="outlined"
                                    fullWidth
                                    required
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    onBlur={(e) => validateField(e.target.name, e.target.value)}
                                    error={!!formErrors.description}
                                    helperText={formErrors.description}
                                    inputProps={{
                                        maxLength: BROADCAST_CHANNEL_LIMITS.description.max,
                                        autoComplete: 'off',
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    fullWidth
                                    disabled={hasFormErrors}
                                    style={{ backgroundColor: 'green', color: 'white' }}
                                >
                                    Add Channel
                                </Button>
                            </Grid>
                        </Grid>
                    </form>
                </Box>

                <Box className="add-checkpoint">
                    <Grid container className='row1' spacing={3}>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="body1" style={{ fontSize: '16px', fontWeight: 500 }}>
                                Broadcast Channels
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container spacing={3} alignItems='center'>
                        <Grid item xs={2} sm={1}>
                            <h4>Sr.no.</h4>
                        </Grid>
                        <Grid item xs={5} sm={3}>
                            <h4>Channel Name</h4>
                        </Grid>
                        <Grid item xs={5} sm={5}>
                            <h4>Channel Description</h4>
                        </Grid>
                    </Grid>
                    <hr />
                    {loadings ? (
                        <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        broadcastChannels.length > 0 && broadcastChannels.map((item, index) => (
                            <Grid container spacing={3} key={item.id ?? index} alignItems='center'>
                                <Grid item xs={2} sm={1}>
                                    {index + 1}
                                </Grid>
                                <Grid item xs={5} sm={3}>
                                    <Typography component="span" variant="body2">
                                        {sanitizeForDisplay(item.name)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={5} sm={5}>
                                    <Typography component="span" variant="body2">
                                        {sanitizeForDisplay(item.description)}
                                    </Typography>
                                </Grid>
                            </Grid>
                        ))
                    )}
                    <hr />
                </Box>
            </Container>
            <ToastContainer />
        </Layout>
    );
};

export default AddBroadCastChannel;
